import SaveOutlined from '@mui/icons-material/SaveOutlined'
import UndoOutlined from '@mui/icons-material/UndoOutlined'
import DownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import SearchIcon from '@mui/icons-material/Search'
import UploadOutlined from '@mui/icons-material/FileUploadOutlined'
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined'
import {
  ColumnsPanelTrigger,
  gridFilteredSortedRowIdsSelector,
  GridRowSelectionModel,
  ToolbarButton,
  Toolbar,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
} from '@mui/x-data-grid-premium'
import { MutableRefObject, useState } from 'react'
import { downloadAsCsv } from 'Common/Utils/utils'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material'
import ViewColumnOutlined from '@mui/icons-material/ViewColumnOutlined'
import FilterListIcon from '@mui/icons-material/FilterList'
import CancelIcon from '@mui/icons-material/Cancel'

const excludedColumns = new Set(['id', 'actions', '__check__'])
const excelMayThrowIssueColumns = new Set(['batchGroupingCode', 'skuCode'])

type AcceptedData = number | string | boolean | null | undefined

export type MUIToolbarComponentProps = {
  hasUnsavedRows: boolean
  isSaving: boolean
  saveChanges: () => void
  discardChanges: () => void
  dataToDownload?: Record<string, AcceptedData>[]
  apiRef?: MutableRefObject<GridApiPremium> | null
  selectedRows: GridRowSelectionModel
  onBulkUpdateButtonClick?: () => void
  uploadData?: (file: File) => void
  /** Opens the grid's own file input. BaseTable passes a ref-scoped opener. */
  onUploadClick?: () => void
  isUploading?: boolean
  isLoading?: boolean
  customDownloadDataMethod?: (
    tableData: Record<string, AcceptedData>[]
  ) => Record<string, AcceptedData>[]
  /** Why the edits can't be saved yet (e.g. a preview found a refused value). Disables Save. */
  saveBlockedReason?: string | null
}

const selectionCount = (selectedRows: GridRowSelectionModel) => {
  if (!selectedRows?.ids) return 0
  return selectedRows.type === 'include' ? selectedRows.ids.size : -1
}

/** A view control on the right: an icon in a 26px square with a tooltip. */
const viewButtonSx = {
  width: 'var(--control-h-sm)',
  height: 'var(--control-h-sm)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--ink-500)',
  '&:hover': { background: 'var(--line-100)', color: 'var(--ink-700)' },
  '& svg': { fontSize: 16 },
} as const

/**
 * The action bar above a grid (design system: GridToolbar). The left side acts
 * on the data, the right side on the view.
 *
 * Left — only what applies: Save and Discard while there are unsaved edits,
 * Bulk update while rows are selected (with the count), Upload when the grid
 * takes one. Right: Columns, Filters (with a count), Download, and a search
 * box that is always open.
 *
 * Replaces a bar that showed Save, Discard and Bulk update permanently
 * (disabled more often than not), in a grey-100 box with its own border
 * inside the grid's border, and a search that had to be clicked open.
 */
export const MUITableToolBar = ({
  hasUnsavedRows,
  isSaving,
  saveChanges,
  discardChanges,
  dataToDownload,
  fileName,
  apiRef = null,
  selectedRows,
  onBulkUpdateButtonClick,
  uploadData,
  onUploadClick,
  isUploading = false,
  isLoading = false,
  customDownloadDataMethod = undefined,
  saveBlockedReason = null,
}: MUIToolbarComponentProps & {
  fileName: string
}) => {
  const selected = selectionCount(selectedRows)
  const dirty = hasUnsavedRows || isSaving

  const openFilePicker = () => {
    if (onUploadClick) {
      onUploadClick()
      return
    }
    ;(
      document.getElementById('base-table-file-upload') as HTMLInputElement
    )?.click()
  }

  return (
    <Box
      sx={{
        background: 'var(--panel-head)',
        borderBottom: '1px solid var(--line-200)',
        '& .MuiDataGrid-toolbar': {
          minHeight: 40,
          px: '8px',
          py: '6px',
          gap: '6px',
          border: 0,
          background: 'transparent',
        },
      }}
    >
      <Toolbar>
        {isLoading ? (
          <CircularProgress size={16} sx={{ mx: '4px' }} />
        ) : (
          <>
            {dirty && (
              <>
                <Tooltip title={saveBlockedReason ?? ''}>
                  {/* A span, so the tooltip still shows on the disabled button. */}
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      loading={isSaving}
                      loadingPosition="start"
                      disabled={
                        !hasUnsavedRows ||
                        isSaving ||
                        Boolean(saveBlockedReason)
                      }
                      onClick={saveChanges}
                      startIcon={<SaveOutlined />}
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small"
                  variant="text"
                  disabled={!hasUnsavedRows || isSaving}
                  onClick={discardChanges}
                  startIcon={<UndoOutlined />}
                >
                  Discard
                </Button>
              </>
            )}
            {selected !== 0 && onBulkUpdateButtonClick && (
              <>
                {dirty && <Divider orientation="vertical" flexItem />}
                <Button
                  size="small"
                  variant="outlined"
                  disabled={isSaving}
                  onClick={onBulkUpdateButtonClick}
                  startIcon={<EditNoteOutlined />}
                >
                  {selected > 0 ? `Bulk update (${selected})` : 'Bulk update'}
                </Button>
              </>
            )}
            {uploadData && (
              <Button
                size="small"
                variant="outlined"
                loading={isUploading}
                loadingPosition="start"
                disabled={isUploading}
                onClick={openFilePicker}
                startIcon={<UploadOutlined />}
              >
                Upload
              </Button>
            )}

            <Box sx={{ flex: 1 }} />

            <Tooltip title="Columns">
              <ColumnsPanelTrigger
                render={<ToolbarButton material={{ sx: viewButtonSx }} />}
              >
                <ViewColumnOutlined />
              </ColumnsPanelTrigger>
            </Tooltip>
            <Tooltip title="Filters">
              <FilterPanelTrigger
                render={(props, state) => (
                  <ToolbarButton
                    {...(props as Omit<typeof props, 'ref'>)}
                    material={{ sx: viewButtonSx }}
                  >
                    <Badge
                      badgeContent={state.filterCount}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: 9,
                          height: 14,
                          minWidth: 14,
                          px: '3px',
                        },
                      }}
                    >
                      <FilterListIcon />
                    </Badge>
                  </ToolbarButton>
                )}
              />
            </Tooltip>
            <ToolBarDownloadCSVButton
              fileName={fileName}
              data={dataToDownload ?? []}
              apiRef={apiRef}
              customDownloadDataMethod={customDownloadDataMethod}
            />
            <QuickFilter defaultExpanded>
              <QuickFilterControl
                render={({ ref, ...controlProps }, state) => (
                  <TextField
                    {...controlProps}
                    inputRef={ref}
                    aria-label="Search"
                    placeholder="Search"
                    size="small"
                    sx={{
                      width: 200,
                      '& .MuiInputBase-root': { height: 'var(--control-h-sm)' },
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 16 }} />
                          </InputAdornment>
                        ),
                        endAdornment: state.value ? (
                          <InputAdornment position="end">
                            <QuickFilterClear
                              edge="end"
                              size="small"
                              aria-label="Clear search"
                            >
                              <CancelIcon sx={{ fontSize: 14 }} />
                            </QuickFilterClear>
                          </InputAdornment>
                        ) : null,
                        ...controlProps.slotProps?.input,
                      },
                      ...controlProps.slotProps,
                    }}
                  />
                )}
              />
            </QuickFilter>
          </>
        )}
      </Toolbar>
    </Box>
  )
}

type ToolBarDownloadCSVButtonProps = {
  fileName: string
  data: Record<string, AcceptedData>[]
  apiRef?: MutableRefObject<GridApiPremium> | null
  customDownloadDataMethod?: (
    data: Record<string, AcceptedData>[]
  ) => Record<string, AcceptedData>[]
}
const ToolBarDownloadCSVButton = ({
  fileName,
  data,
  apiRef = null,
  customDownloadDataMethod = undefined,
}: ToolBarDownloadCSVButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false)

  const prepareTableData = () => {
    if (!apiRef) {
      return []
    }
    if (customDownloadDataMethod) {
      return customDownloadDataMethod(data)
    }
    // The visible columns, over the filtered and sorted rows, in that order.
    const visibleColumns = apiRef.current.getVisibleColumns()
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef)
    const rowModels = apiRef.current.getRowModels()

    return rowIds.map((id) => {
      const row = rowModels.get(id)
      const rowData: Record<string, AcceptedData> = {}
      if (row) {
        visibleColumns.forEach((col) => {
          if (excludedColumns.has(col.field)) {
            return
          }
          // What the grid shows, not the raw row field: computed columns
          // (valueGetter) exported empty, and object-valued columns as
          // "[object Object]". Objects fall back to the column's formatter.
          let value: unknown = apiRef.current.getRowValue(row, col)
          if (value !== null && typeof value === 'object') {
            value = apiRef.current.getRowFormattedValue(row, col)
          }
          if (value !== null && typeof value === 'object') {
            value = JSON.stringify(value)
          }
          if (excelMayThrowIssueColumns.has(col.field)) {
            rowData[col.field] = `'${value}`
            return
          }
          rowData[col.field] = value as AcceptedData
        })
      }
      return rowData
    })
  }

  const downloadFile = () => {
    setIsDownloading(true)
    try {
      downloadAsCsv(fileName, apiRef ? prepareTableData() : data)
    } catch (error) {
      console.error('Error preparing or downloading CSV:', error)
    }
    setIsDownloading(false)
  }

  return (
    <Tooltip title="Download CSV">
      <ToolbarButton
        onClick={downloadFile}
        disabled={isDownloading}
        aria-label="Download CSV"
        material={{ sx: viewButtonSx }}
      >
        {isDownloading ? <CircularProgress size={14} /> : <DownloadOutlined />}
      </ToolbarButton>
    </Tooltip>
  )
}
